import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ApolloError, ApolloQueryResult } from '@apollo/client';
import Cookies from 'js-cookie';
import { useSearchParams } from 'react-router-dom';

import { useAnalyticsContext } from 'app/shared/utils';
import { useVisaAuth } from 'app/shared/utils/visa';
import {
  Event,
  EventAttendee,
  GetEventAttendeeData,
  GetEventAttendeeVariables,
} from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { GetEventAttendeeForFan } from 'app/fan/graphql/eventAttendees/queryHooks';
import { GetEventForFan } from 'app/fan/graphql/events/queryHooks';

export interface EventPageContextValue {
  event?: Event;
  eventLoading: boolean;
  eventLoadError?: ApolloError;
  eventAttendee?: EventAttendee;
  eventAttendeeLoading: boolean;
  eventAttendeeLoadError?: ApolloError;
  refetchEventAttendee?: (
    variables?: GetEventAttendeeVariables
  ) => Promise<ApolloQueryResult<GetEventAttendeeData>>;
  forceStartedEventState: boolean;
  isAddToAccountFlow: boolean;
  isPlusOneFlow: boolean;
  isGuestAndHasTickets: boolean;
  ticketId?: number;
  token?: string;

  shouldDisplayVisaDisclaimer?: boolean;
  setShouldDisplayVisaDisclaimer?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

interface EventPageContextProviderProps {
  eventId?: string;
}

export const EventPageContext = createContext<EventPageContextValue | null>(
  null
);

export const useEventPageContext: () => EventPageContextValue = () => {
  const value = useContext(EventPageContext);

  if (value == null) {
    throw new Error(
      'useEventPageContext must be called within a child or grandchild of an EventPageContext.Provider!'
    );
  }
  return value;
};

export const EventPageContextProvider: React.FC<
  React.PropsWithChildren<EventPageContextProviderProps>
> = ({ eventId, children }) => {
  const { user: loggedInUser } = useContext(AuthContext);
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const [searchParams] = useSearchParams();
  const eventAttendeeId = Number(searchParams.get('eaid')) || undefined;
  const ticketId = Number(searchParams.get('tid')) || undefined;
  const forceStartedEventState = searchParams.get('sofarStarted') == 'true';
  const [firedCameFromShareLinkEvent, setFiredCameFromShareLinkEvent] =
    useState(false);

  const isPlusOneFlow = !!eventAttendeeId;

  const token = searchParams.get('token') || undefined;
  const isAddToAccountFlow =
    !!token && searchParams.get('add_to_account') == 'true';

  useEffect(() => {
    if (isPlusOneFlow && !firedCameFromShareLinkEvent) {
      trackAnalyticsEvent('Came From Guest Share Link', {});
      setFiredCameFromShareLinkEvent(true);
    }
  }, [firedCameFromShareLinkEvent, isPlusOneFlow, trackAnalyticsEvent]);

  const variables = forceStartedEventState
    ? { id: Number(eventId), includeTodaysArtists: true }
    : { id: Number(eventId) };
  const {
    data: eventData,
    loading: eventDataLoading,
    error: eventDataLoadError,
  } = GetEventForFan(variables);

  const event = eventData?.event;

  const {
    guest_last_purchased_num_tickets: guestLastPurchasedNumTickets,
    last_purchased_user_id: lastPurchasedUserId,
    last_purchased_event_id: lastPurchasedEventId,
  } = Cookies.get();
  const isGuestAndHasTickets =
    !loggedInUser &&
    lastPurchasedEventId == eventId &&
    parseInt(guestLastPurchasedNumTickets) > 0 &&
    lastPurchasedUserId.length > 0;

  const userId = isGuestAndHasTickets
    ? lastPurchasedUserId
    : loggedInUser
      ? String(loggedInUser.id)
      : undefined;

  const visibleToUser = isGuestAndHasTickets ? undefined : true;
  const shouldSkipGetEventAttendee =
    isAddToAccountFlow || !(eventAttendeeId || (eventId && userId));
  const getEventAttendeeVariables = {
    id: eventAttendeeId,
    eventId,
    userId,
    visibleToUser,
    skip: shouldSkipGetEventAttendee,
  };

  const {
    data: eventAttendeeData,
    loading: eventAttendeeLoading,
    error: eventAttendeeLoadError,
    refetch: refetchEventAttendee,
  } = GetEventAttendeeForFan(getEventAttendeeVariables);

  const { visaPreSaleEligible, canBypassVisaValidation } = useVisaAuth({
    eventId: Number(eventId),
    skipLoadCardRedemptionsStatus: true,
  });

  const defaultShouldDisplayVisaDisclaimer =
    visaPreSaleEligible || (!!event?.onPresale && canBypassVisaValidation);
  const [shouldDisplayVisaDisclaimer, setShouldDisplayVisaDisclaimer] =
    useState(defaultShouldDisplayVisaDisclaimer);

  useEffect(() => {
    if (!shouldDisplayVisaDisclaimer && defaultShouldDisplayVisaDisclaimer) {
      setShouldDisplayVisaDisclaimer(true);
    }
  }, [defaultShouldDisplayVisaDisclaimer]);

  const eventPageV2ContextProvider = useMemo(
    () => ({
      event: eventData?.event,
      eventLoading: eventDataLoading,
      eventLoadError: eventDataLoadError,
      eventAttendee: eventAttendeeData?.eventAttendee,
      eventAttendeeLoading,
      eventAttendeeLoadError,
      refetchEventAttendee,
      forceStartedEventState,
      isAddToAccountFlow,
      isPlusOneFlow,
      isGuestAndHasTickets,
      ticketId,
      token,
      shouldDisplayVisaDisclaimer,
      setShouldDisplayVisaDisclaimer,
    }),
    [
      eventData,
      eventDataLoading,
      eventDataLoadError,
      eventAttendeeData,
      eventAttendeeLoading,
      eventAttendeeLoadError,
      refetchEventAttendee,
      forceStartedEventState,
      isAddToAccountFlow,
      isPlusOneFlow,
      isGuestAndHasTickets,
      ticketId,
      token,
      shouldDisplayVisaDisclaimer,
      setShouldDisplayVisaDisclaimer,
    ]
  );

  return (
    <EventPageContext.Provider value={eventPageV2ContextProvider}>
      {children}
    </EventPageContext.Provider>
  );
};

export default EventPageContextProvider;
